import {Button} from '@/components/ui/button/Button'
import {ArrowLeftIcon, Download01Icon} from '@/components/ui/icon/Icon'
import {Spinner} from '@/components/ui/spinner/Spinner'
import {Checkbox} from '@/components/commons/checkbox/ChekBox'
import {useTranslation} from '@/translations/i18n'
import {SiarExportPreviewItem} from '@/features/projects/siar-bulk-export/types'
import {HttpGetSiarBulkExportPreviewCountersResponse} from '@/features/projects/siar-bulk-export/http/siarBulkExport.http'
import {BulkExportConfig} from '@/features/projects/siar-bulk-export/components/bulk-siar-export-dialog/BulkSiarExportConfigStep'
import {PreviewFilterTab} from '@/features/projects/siar-bulk-export/components/bulk-siar-export-dialog/BulkSiarExportDialog'
import {BulkSiarExportCriteriaSummary} from '@/features/projects/siar-bulk-export/components/bulk-siar-export-dialog/BulkSiarExportCriteriaSummary'
import {BulkSiarExportPreviewTable} from '@/features/projects/siar-bulk-export/components/bulk-siar-export-dialog/BulkSiarExportPreviewTable'
import {BulkSiarExportPreviewTableSkeleton} from '@/features/projects/siar-bulk-export/components/bulk-siar-export-dialog/BulkSiarExportPreviewTableSkeleton'
import {BulkSiarExportInOtherBatchBanner} from '@/features/projects/siar-bulk-export/components/bulk-siar-export-dialog/BulkSiarExportInOtherBatchBanner'
import {
    StyledBulkSiarExportBackButton,
    StyledBulkSiarExportFilterTab,
    StyledBulkSiarExportFilterTabBar,
    StyledBulkSiarExportOpeningFilterRow,
    StyledBulkSiarExportPreviewBody,
    StyledBulkSiarExportPreviewCounterRow,
    StyledBulkSiarExportPreviewEmptyState,
    StyledBulkSiarExportPreviewFooter,
    StyledBulkSiarExportToggleAllButton
} from '@/features/projects/siar-bulk-export/components/bulk-siar-export-dialog/style'

type BulkSiarExportPreviewStepProps = {
    config: BulkExportConfig
    items: SiarExportPreviewItem[]
    total: number
    isLoading: boolean
    counters: HttpGetSiarBulkExportPreviewCountersResponse | undefined
    isCountersLoading: boolean
    activeTab: PreviewFilterTab
    onTabChange: (tab: PreviewFilterTab) => void
    onlyOpeningTransmitted: boolean
    onOnlyOpeningTransmittedChange: (value: boolean) => void
    isSelected: (projectId: string) => boolean
    selectedCount: number
    onMasterToggle: () => void
    onToggle: (projectId: string) => void
    onIncludeOtherBatch: () => void
    onIgnoreOtherBatch: () => void
    selectedInOtherBatchCount: number
    onLoadMore: () => void
    onBack: () => void
    onExport: () => void
    isExportPending: boolean
}

export const BulkSiarExportPreviewStep = ({
    config,
    items,
    isLoading,
    counters,
    isCountersLoading,
    activeTab,
    onTabChange,
    onlyOpeningTransmitted,
    onOnlyOpeningTransmittedChange,
    isSelected,
    selectedCount,
    onMasterToggle,
    onToggle,
    onIncludeOtherBatch,
    onIgnoreOtherBatch,
    selectedInOtherBatchCount,
    onLoadMore,
    onBack,
    onExport,
    isExportPending
}: BulkSiarExportPreviewStepProps) => {
    const {t} = useTranslation()

    const eligibleTotal = counters ? counters.toExport + counters.toTransmit : 0
    const allDeselected = selectedCount === 0

    // Master checkbox reflects loaded items only
    const loadedSelectedCount = items.filter(item => isSelected(item.projectId)).length
    const masterChecked = items.length > 0 && loadedSelectedCount === items.length
    const masterIndeterminate = loadedSelectedCount > 0 && loadedSelectedCount < items.length

    const isInitialLoading = isLoading && items.length === 0
    const showCounters = !isCountersLoading && !!counters
    const showBanner = selectedInOtherBatchCount > 0
    // The "only opening transmitted" filter is shown only for closing exports.
    const isClosingExport = config.exportType === 'close_project'

    return (
        <>
            <StyledBulkSiarExportPreviewBody>
                <BulkSiarExportCriteriaSummary config={config} />
                {showBanner && (
                    <BulkSiarExportInOtherBatchBanner
                        count={selectedInOtherBatchCount}
                        onInclude={onIncludeOtherBatch}
                        onIgnore={onIgnoreOtherBatch}
                    />
                )}
                {showCounters && (
                    <StyledBulkSiarExportPreviewCounterRow>
                        <span>
                            {t('projects:bulk_siar_export_selected_counter', {
                                selected: selectedCount,
                                total: eligibleTotal
                            })}
                        </span>
                        {eligibleTotal > 0 && (
                            <StyledBulkSiarExportToggleAllButton type="button" onClick={onMasterToggle}>
                                {allDeselected
                                    ? t('projects:bulk_siar_export_select_all')
                                    : t('projects:bulk_siar_export_deselect_all')}
                            </StyledBulkSiarExportToggleAllButton>
                        )}
                    </StyledBulkSiarExportPreviewCounterRow>
                )}
                <StyledBulkSiarExportFilterTabBar>
                    <StyledBulkSiarExportFilterTab
                        type="button"
                        $active={activeTab === 'all'}
                        onClick={() => onTabChange('all')}
                    >
                        {t('projects:bulk_siar_export_preview_tab_all')}
                        {showCounters && ` (${counters.total})`}
                    </StyledBulkSiarExportFilterTab>
                    <StyledBulkSiarExportFilterTab
                        type="button"
                        $active={activeTab === 'to_export'}
                        onClick={() => onTabChange('to_export')}
                    >
                        {t('projects:bulk_siar_export_preview_tab_to_export')}
                        {showCounters && ` (${counters.toExport})`}
                    </StyledBulkSiarExportFilterTab>
                    <StyledBulkSiarExportFilterTab
                        type="button"
                        $active={activeTab === 'to_transmit'}
                        onClick={() => onTabChange('to_transmit')}
                    >
                        {t('projects:bulk_siar_export_preview_tab_to_transmit')}
                        {showCounters && ` (${counters.toTransmit})`}
                    </StyledBulkSiarExportFilterTab>
                </StyledBulkSiarExportFilterTabBar>
                {isClosingExport && (
                    <StyledBulkSiarExportOpeningFilterRow>
                        <Checkbox
                            id="bulk-siar-export-only-opening-transmitted"
                            label={t('projects:bulk_siar_export_only_opening_transmitted')}
                            checked={onlyOpeningTransmitted}
                            onChange={() => onOnlyOpeningTransmittedChange(!onlyOpeningTransmitted)}
                        />
                    </StyledBulkSiarExportOpeningFilterRow>
                )}
                {isInitialLoading ? (
                    <BulkSiarExportPreviewTableSkeleton />
                ) : items.length === 0 ? (
                    <StyledBulkSiarExportPreviewEmptyState>
                        {t('projects:bulk_siar_export_preview_empty')}
                    </StyledBulkSiarExportPreviewEmptyState>
                ) : (
                    <BulkSiarExportPreviewTable
                        items={items}
                        isSelected={isSelected}
                        onToggle={onToggle}
                        onToggleAll={onMasterToggle}
                        masterChecked={masterChecked}
                        masterIndeterminate={masterIndeterminate}
                        onLoadMore={onLoadMore}
                    />
                )}
            </StyledBulkSiarExportPreviewBody>
            <StyledBulkSiarExportPreviewFooter>
                <StyledBulkSiarExportBackButton type="button" onClick={onBack}>
                    <ArrowLeftIcon />
                    {t('projects:bulk_siar_export_back')}
                </StyledBulkSiarExportBackButton>
                <Button variant="primaryNeutral" onClick={onExport} disabled={selectedCount === 0 || isExportPending}>
                    <Download01Icon />
                    {t('projects:bulk_siar_export_export_cta', {count: selectedCount})}
                    {isExportPending && <Spinner />}
                </Button>
            </StyledBulkSiarExportPreviewFooter>
        </>
    )
}
